import React from "react";
import { Box, CircularProgress, Divider } from "@mui/material";
import { Add as AddIcon, Check as CheckIcon } from "@mui/icons-material";
import AdaptivePopover from "../../common/AdaptivePopover";
import MenuRow from "../../common/MenuRow";
import { useAddStockToWatchlist } from "./hooks/useAddStockToWatchlist";
import { useRemoveStockFromWatchlist } from "./hooks/useRemoveStockFromWatchlist";
import { Watchlist } from "./hooks/useWatchlists";
import { WatchlistMembership } from "./hooks/useIsStockInWatchlist";
import { MAX_HOLDINGS_PER_WATCHLIST, MAX_WATCHLISTS_PER_USER } from "./watchlistLimits";
import { hapticError, hapticSelection } from "../../../lib/haptics";

interface MembershipRowProps {
  watchlist: Watchlist;
  ticker: string;
  membership: WatchlistMembership | undefined;
  onDone: () => void;
}

/**
 * One watchlist row in the picker.
 *
 * This is a component rather than inline JSX because each row needs its own
 * add/remove mutations, and hooks can't be called inside a .map().
 */
const MembershipRow: React.FC<MembershipRowProps> = ({
  watchlist,
  ticker,
  membership,
  onDone,
}) => {
  const addStock = useAddStockToWatchlist(watchlist.id);
  const removeStock = useRemoveStockFromWatchlist(watchlist.id);

  const isMember = !!membership;
  const isPending = addStock.isPending || removeStock.isPending;
  const isFull = !isMember && watchlist.holdings_count >= MAX_HOLDINGS_PER_WATCHLIST;

  const handleClick = () => {
    const callbacks = {
      onSuccess: () => {
        hapticSelection();
        onDone();
      },
      onError: () => hapticError(),
    };

    if (isMember && membership) {
      removeStock.mutate({ holdingId: membership.holdingId, ticker }, callbacks);
      return;
    }
    addStock.mutate({ ticker }, callbacks);
  };

  const trailing = () => {
    if (isPending) return <CircularProgress size={16} sx={{ color: "#22c55e" }} />;
    if (isMember) return <CheckIcon fontSize="small" sx={{ color: "#22c55e" }} />;
    return null;
  };

  return (
    <MenuRow
      label={watchlist.name}
      detail={`${watchlist.holdings_count} ${watchlist.holdings_count === 1 ? "stock" : "stocks"}`}
      selected={isMember}
      disabled={isPending || isFull}
      disabledReason={isFull ? `Full — ${MAX_HOLDINGS_PER_WATCHLIST} stocks maximum` : undefined}
      trailing={trailing()}
      onClick={handleClick}
    />
  );
};

interface WatchlistMembershipPickerProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  ticker: string;
  watchlists: Watchlist[];
  memberships: WatchlistMembership[];
  onCreate: () => void;
}

/**
 * Picks which watchlists a ticker belongs to. A stock can be in several, so
 * each row toggles only its own list — never a blanket add or remove.
 */
const WatchlistMembershipPicker: React.FC<WatchlistMembershipPickerProps> = ({
  open,
  anchorEl,
  onClose,
  ticker,
  watchlists,
  memberships,
  onCreate,
}) => {
  const membershipByWatchlistId = new Map(
    memberships.map((membership) => [membership.watchlistId, membership]),
  );

  return (
    <AdaptivePopover open={open} anchorEl={anchorEl} onClose={onClose} title={`Save ${ticker} to`}>
      <Box role="menu">
        {watchlists.map((watchlist) => (
          <MembershipRow
            key={watchlist.id}
            watchlist={watchlist}
            ticker={ticker}
            membership={membershipByWatchlistId.get(watchlist.id)}
            onDone={onClose}
          />
        ))}

        <Divider sx={{ my: 0.5 }} />

        <MenuRow
          icon={<AddIcon fontSize="small" />}
          label="New watchlist"
          disabled={watchlists.length >= MAX_WATCHLISTS_PER_USER}
          disabledReason={`You've reached the limit of ${MAX_WATCHLISTS_PER_USER} watchlists`}
          onClick={() => {
            onClose();
            onCreate();
          }}
        />
      </Box>
    </AdaptivePopover>
  );
};

export default WatchlistMembershipPicker;
